/**
 * Service de cache pour les données tenant
 * Utilise SessionStorage + Cache mémoire avec invalidation intelligente
 */

import { TenantData } from '../hooks/useTenant';

const CACHE_KEY = 'sis_tenant_cache';
const CACHE_VERSION_KEY = 'sis_tenant_cache_version';

// Cache mémoire (niveau 1)
let memoryCache: TenantData | null = null;
let cacheVersion: string | null = null;

/**
 * Génère une version de cache basée sur le tenant ID et timestamp
 */
function generateCacheVersion(tenantId: string): string {
  return `${tenantId}_${Date.now()}`;
}

/**
 * Récupère la version actuelle du cache
 */
function getCurrentCacheVersion(): string | null {
  try {
    return sessionStorage.getItem(CACHE_VERSION_KEY);
  } catch (error) {
    console.warn('Erreur lors de la lecture de la version du cache:', error);
    return null;
  }
}

/**
 * Vérifie si le cache est valide pour le tenant donné
 */
function isCacheValid(tenantId: string): boolean {
  const storedVersion = getCurrentCacheVersion();
  if (!storedVersion) return false;
  
  // Vérifier que la version contient le bon tenant ID
  return storedVersion.startsWith(`${tenantId}_`);
}

/**
 * Récupère les données du cache (mémoire puis SessionStorage)
 */
export function getTenantFromCache(tenantId: string): TenantData | null {
  try {
    // Niveau 1: Cache mémoire
    if (memoryCache && isCacheValid(tenantId)) {
      console.log('🚀 Cache tenant: Récupération depuis la mémoire');
      return memoryCache;
    }

    // Niveau 2: SessionStorage
    if (!isCacheValid(tenantId)) {
      console.log('📝 Cache tenant: Version invalide, nettoyage');
      clearTenantCache();
      return null;
    }

    const cachedData = sessionStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const parsedData: TenantData = JSON.parse(cachedData);
      
      // Vérifier que les données correspondent au tenant demandé
      if (parsedData.id === tenantId) {
        console.log('💾 Cache tenant: Récupération depuis SessionStorage');
        // Restaurer le cache mémoire
        memoryCache = parsedData;
        return parsedData;
      }
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du cache tenant:', error);
    // En cas d'erreur, nettoyer le cache corrompu
    clearTenantCache();
    return null;
  }
}

/**
 * Sauvegarde les données tenant dans le cache
 */
export function setTenantCache(tenantData: TenantData): void {
  try {
    const newVersion = generateCacheVersion(tenantData.id);
    
    // Sauvegarder en mémoire (niveau 1)
    memoryCache = tenantData;
    cacheVersion = newVersion;
    
    // Sauvegarder en SessionStorage (niveau 2)
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(tenantData));
    sessionStorage.setItem(CACHE_VERSION_KEY, newVersion);
    
    console.log('✅ Cache tenant: Données sauvegardées', {
      tenantId: tenantData.id,
      tenantName: tenantData.name,
      version: newVersion
    });
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du cache tenant:', error);
    // En cas d'erreur de stockage, au moins garder le cache mémoire
    memoryCache = tenantData;
  }
}

/**
 * Invalide et nettoie tout le cache tenant
 */
export function clearTenantCache(): void {
  try {
    // Nettoyer le cache mémoire
    memoryCache = null;
    cacheVersion = null;
    
    // Nettoyer SessionStorage
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_VERSION_KEY);
    
    console.log('🧹 Cache tenant: Cache nettoyé');
  } catch (error) {
    console.error('Erreur lors du nettoyage du cache tenant:', error);
  }
}

/**
 * Invalide le cache pour un tenant spécifique
 */
export function invalidateTenantCache(tenantId: string): void {
  const currentVersion = getCurrentCacheVersion();
  
  if (currentVersion && currentVersion.startsWith(`${tenantId}_`)) {
    console.log('🔄 Cache tenant: Invalidation pour tenant', tenantId);
    clearTenantCache();
  }
}

/**
 * Obtient des informations sur l'état du cache
 */
export function getCacheInfo(): {
  hasMemoryCache: boolean;
  hasSessionCache: boolean;
  version: string | null;
  tenantId: string | null;
} {
  const version = getCurrentCacheVersion();
  const tenantId = version ? version.split('_')[0] : null;
  
  return {
    hasMemoryCache: memoryCache !== null,
    hasSessionCache: !!sessionStorage.getItem(CACHE_KEY),
    version,
    tenantId
  };
}

/**
 * Hook pour déboguer le cache (développement uniquement)
 */
export function debugCache(): void {
  if (process.env.NODE_ENV === 'development') {
    const info = getCacheInfo();
    console.log('🔍 Debug Cache Tenant:', {
      ...info,
      memoryCache: memoryCache ? { id: memoryCache.id, name: memoryCache.name } : null
    });
  }
}
